import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useGetStoreQuery,
  useGetWorkspaceQuery,
  useUpdateWorkspaceMutation,
} from '@/store/api/directoryApi';

export function WorkspacesPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    data: workspace,
    isLoading,
    isError,
    refetch,
  } = useGetWorkspaceQuery(workspaceId ?? '', { skip: !workspaceId });
  const { data: store } = useGetStoreQuery(workspace?.storeId ?? '', { skip: !workspace?.storeId });
  const [updateWorkspace, { isLoading: isSaving }] = useUpdateWorkspaceMutation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', kind: '', status: '' });

  useEffect(() => {
    if (workspace) {
      setForm({ name: workspace.name, kind: workspace.kind, status: workspace.status });
    }
  }, [workspace]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!workspaceId || !form.name.trim() || !form.kind.trim() || !form.status.trim()) {
      return;
    }
    try {
      await updateWorkspace({
        id: workspaceId,
        body: { name: form.name.trim(), kind: form.kind.trim(), status: form.status.trim() },
      }).unwrap();
      toast.success('Workspace settings saved');
      setEditing(false);
    } catch {
      toast.error('Could not save this workspace. Your changes are still here; try again.');
    }
  }

  if (isLoading) {
    return <LoadingSpinner className="py-12" />;
  }
  if (isError || !workspace) {
    return (
      <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
        Could not load this workspace.{' '}
        <Button
          variant="link"
          onClick={() => {
            void refetch();
          }}
        >
          <RefreshCw className="mr-1 h-3 w-3" /> Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/stores')}>
        <ArrowLeft className="mr-2 h-4 w-4" /> Stores &amp; workspaces
      </Button>
      <div>
        <h1 className="text-2xl font-bold">{workspace.name}</h1>
        <p className="text-sm text-muted-foreground">Workspace overview and settings.</p>
      </div>
      {editing ? (
        <Card>
          <CardContent className="pt-6">
            <form
              className="max-w-xl space-y-4"
              onSubmit={(event) => {
                void save(event);
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="workspace-name">Name</Label>
                <Input
                  id="workspace-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="workspace-kind">Kind</Label>
                <Input
                  id="workspace-kind"
                  required
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="workspace-status">Status</Label>
                <Input
                  id="workspace-status"
                  required
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Use a status accepted by your organization’s workspace policy.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" type="button" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? 'Saving…' : 'Save workspace'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <dl className="grid gap-4 sm:grid-cols-2">
              <Detail label="Workspace ID" value={workspace.id} mono />
              <Detail label="Status" value={workspace.status} />
              <Detail label="Kind" value={workspace.kind} />
              <Detail label="Store" value={store?.name ?? workspace.storeId} />
            </dl>
            <div className="flex justify-end border-t pt-4">
              <Button variant="outline" onClick={() => setEditing(true)}>
                Edit settings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      <p className="text-sm text-muted-foreground">
        Simple roles are managed at the organization scope from the related store’s settings.
      </p>
      {store && (
        <Button asChild variant="link" className="px-0">
          <Link to={`/stores/${store.id}/roles`}>Open simple roles for {store.name}</Link>
        </Button>
      )}
    </div>
  );
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm ${mono ? 'break-all font-mono' : ''}`}>{value || '—'}</dd>
    </div>
  );
}
